import React, { useState, useCallback, useEffect } from "react";

import {
    Box, 
    TextField,
    Grid,
    Button,
    Switch,
    FormControlLabel,
    styled,
    Card,
    List,
    ListSubheader,
    ListItem,
    ListItemButton,
    IconButton,
    Skeleton,
} from '@mui/material';

import { grey } from "@mui/material/colors";

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import { CommonIcons, icons } from "../../common/CommonIcons";
import CommonRestricted from '../../common/CommonRestricted';

import { useItems, itemTypes, targetTypes } from "../../../hooks/api/useItems";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';
import { initialValues, validationSchema } from "../../../schema/products/categorySchema";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

import NewCategoryChildren from "./children/NewCategoryChildren"
import UpdateCategoryChildren from "./children/UpdateCategoryChildren"

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const StyledCard2 = styled(Card)(({ theme }) => ({
    padding: theme.spacing(0)
}));

const StyledListHeader = styled(ListSubheader)(({ theme }) => ({
    // padding: theme.spacing(0),
    backgroundColor: grey[100],
    textTransform: 'capitalize',
}));


export default function UpdateCategory({
    routeType
}) {

    const { t } = useTranslations({ ns: ["common", "@product"] })

    const { id } = useParams();
    const { promptMessage } = useSystemPrompt();
    const { isLoading, update, get, remove } = useItems({ itemType: itemTypes.category, targetType: targetTypes.category });
    const { setSystemLoading, openCustomDrawer, closeCustomDrawer } = useAppContextFeatures();

    const navigate = useNavigate();
    const [ savedValues, setSavedValues ] = useState(null)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.category.id)

    const [ loadingChildren, setLoadingChildren ] = useState(false)

    const [ child, setChild ] = useState({
        level1: [],
        level2: []
    })

    const [ selectedId, setSelectedId ] = useState("")

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try{ 

            const { data } = await get({ id, dataTypesArray: ["parent_category", "child_category"] })
            const { item_category: itemCategory } = data;
            const { child_categories: childCategory = [] } = itemCategory

            setSavedValues({
                categoryId: itemCategory?.category_id,
                parentCategoryId: itemCategory?.parentCategoryId,
                categoryName: itemCategory?.name,
                description: itemCategory?.description,
                status: itemCategory?.status === '1'
            })

            setChild(prev => ({...prev, level1: childCategory }))

        }catch(error){
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const fetchChildren = useCallback(async (id) => {

        if(!id) return false

        // setSystemLoading(true)
        setLoadingChildren(true)

        try{ 

            const { data } = await get({ id, dataTypesArray: ["child_category"] })
            const { item_category: itemCategory } = data;
            const { child_categories: childCategory = [] } = itemCategory

            setChild(prev => ({...prev, level2: childCategory }))
            setSelectedId(id)

        }catch(error){
            promptMessage(false, `${error?.message}`)
        }

        // setSystemLoading(false)
        setLoadingChildren(false)

        return true;

    }, [id])

    useEffect(() => {

        loadData(id);

    }, [])

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        try{
            let data = {
                item_category: {
                    category_id: id,
                    parent_category_id: 0,
                    name: currentValues?.categoryName,
                    description: currentValues?.description,
                    status: currentValues?.status? 1 : 0,
                }
            }
    
            const response = await update({ id, data });
    
            if(response?.status){
                
                promptMessage(true, `${response?.message}`)
                // navigate(getRoute("products/categories"))

            }            

        }catch(error){
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;  

    const handleItemEdit = ({ categoryId, parentCategoryId }) => {

        openCustomDrawer({ 
            show: true, 
            anchor: "right",
            title: t('updateCategory'),
            PaperProps: {
                sx: { width: 2/3 }
            },
            handleOnClose: () => {},
            ComponentActions:
                <></>,
            ComponentBody: 

                <UpdateCategoryChildren
                    routeType={routeType}
                    categoryId={categoryId}
                    parentCategoryId={parentCategoryId}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        loadData(id)

                        fetchChildren(selectedId)
                    }}
                />

        })

    }

    const handleItemNew = ({ parentId }) => {

        openCustomDrawer({ 
            show: true, 
            anchor: "right",
            title: t('newCategory'),
            PaperProps: {
                sx: { width: 2/3 }
            },
            handleOnClose: () => {},
            ComponentActions:
                <></>,
            ComponentBody: 
                <NewCategoryChildren
                    routeType={routeType}
                    parentCategoryId={parentId}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        loadData(id)
                        fetchChildren(selectedId)
                    }}
                />
        })

    }

    const handleItemDelete = async ({ categoryId, loadDataId = '', fetchChildrenId = '' }) => {

        setSystemLoading(true)

        try{
            
            let result = await remove({ ids: [categoryId] })
            promptMessage(true, result?.message)    

            if(loadDataId){
                loadData(loadDataId)
                setChild(prev => ({...child, level2: []}))
                setSelectedId("")
            }

            if(fetchChildrenId)
                fetchChildren(fetchChildrenId)

        }catch(error){
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const RenderListDataItemSkeleton = () => {
        return (
            <ListItem
                disablePadding
                divider
            >
                <Skeleton 
                    animation="wave" 
                    // variant="circular" 
                    sx={{
                        // minWidth: 100,
                        width: '100%',  
                        // mr: 1,
                        height: 40,
                        mx: 1
                    }}
                />
            </ListItem>
        )
    }

    const handleLevel1Click = ({ id }) => {
        fetchChildren(id)
    }

    const RenderCategoryNotSelected = () => {
        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                        {t('youHaveNotSelectAnyParentCategory')}
                    </Box>
                </Box>
            </StyledCard2>
        )
    }

    const RenderCategoryEmpty = ({ parentId }) => {
        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                       {t('youHaveNotCreatedAnyItemYet')}
                    </Box>
                    <Button
                        variant="outlined"
                        sx={{
                            mt: 1
                        }}
                        onClick={() => handleItemNew({ parentId })}
                    >
                        {t('createNew')}
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderCategoryLevel1 = () => {

        if(!child.level1.length) return <RenderCategoryEmpty parentId={id} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    // subheader={<li />}
                    disablePadding
                >
                    <StyledListHeader>{`Level 1`}</StyledListHeader>
                    {
                        child.level1.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                    onClick={() => handleLevel1Click({ id: i.id })}
                                    selected={
                                        selectedId === i.id
                                    }
                                >
                                    <Grid container spacing={1}> 
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
                                            {`${i.name}`}
                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>

                                            {
                                                isPermissionAllow({ functionType: functionTypes.pageChildrenUpdate.id }) &&

                                                <IconButton
                                                    color={"primary"}
                                                    size="small"
                                                    onClick={() => handleItemEdit({ categoryId: i.id, parentCategoryId: i.parent_category_id }) }
                                                >
                                                    <CommonIcons name={icons.edit} />
                                                </IconButton>
                                            }

                                            {
                                                isPermissionAllow({ functionType: functionTypes.removeChildren.id }) &&

                                                <IconButton
                                                    color={"error"}
                                                    size="small"
                                                    onClick={() => handleItemDelete({ categoryId: i.id, loadDataId: id }) }
                                                    sx={{
                                                        mr: 2
                                                    }}
                                                >
                                                    <CommonIcons name={icons.remove} />
                                                </IconButton>
                                            }
                                           
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>

                {
                    isPermissionAllow({ functionType: functionTypes.pageChildrenNew.id }) &&

                    <Box
                        sx={{ m: 1 }}
                    >
                        <Button
                            variant={"outlined"}
                            size="medium"
                            fullWidth
                            onClick={() => handleItemNew({ parentId: id })}
                        >   
                            <CommonIcons name={icons.add} />
                            <Box sx={{ ml: 1 }}>
                                {t('new')}
                            </Box>
                        </Button>
                    </Box>
                }
            </StyledCard2>
        )
    }

    const RenderCategoryLevel2 = () => {

        // check is loading
        if(loadingChildren){
            return (
                <StyledCard2>
                    <List
                        sx={{
                            width: '100%',
                            // backgroundColor: 'red',
                            // maxWidth: 360,
                            // bgcolor: 'background.paper',
                            position: 'relative',
                            overflow: 'auto',
                            // height: '100%',
                            minHeight: 300,
                            '& ul': { padding: 0 },
                        }}
                        // subheader={<li />}
                        disablePadding
                    >
                    <StyledListHeader>{`Level 2`}</StyledListHeader>
                    {
                        [0,1,2,3].map((i, index) => {
                            return (
                                <RenderListDataItemSkeleton key={index} />
                            )
                        })
                    }
                    </List>
                </StyledCard2>
            )
        }

        if(!selectedId) return <RenderCategoryNotSelected />
        if(!child.level2.length) return <RenderCategoryEmpty parentId={selectedId} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        // backgroundColor: 'red',
                        // maxWidth: 360,
                        // bgcolor: 'background.paper',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    // subheader={<li />}
                    disablePadding
                >
                    <StyledListHeader>{`Level 2`}</StyledListHeader>
                    {
                        child.level2.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                >
                                    <Grid container spacing={1}> 
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
                                            {`${i.name}`}
                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>

                                            {
                                                isPermissionAllow({ functionType: functionTypes.pageChildrenUpdate.id }) &&
                                                
                                                <IconButton
                                                    color={"primary"}
                                                    size="small"
                                                    onClick={() => handleItemEdit({ categoryId: i.id, parentCategoryId: i.parent_category_id }) }
                                                >
                                                    <CommonIcons name={icons.edit} />
                                                </IconButton>
                                            }

                                            {
                                                isPermissionAllow({ functionType: functionTypes.removeChildren.id }) &&

                                                <IconButton
                                                    color={"error"}
                                                    size="small"
                                                    onClick={() => handleItemDelete({ categoryId: i.id, fetchChildrenId: i.parent_category_id }) }
                                                    sx={{
                                                        mr: 2
                                                    }}
                                                >
                                                    <CommonIcons name={icons.remove} />
                                                </IconButton>
                                            }
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>
                <Box
                    sx={{ m: 1 }}
                >
                    <Button
                        variant={"outlined"}
                        size="medium"
                        fullWidth
                        
                        onClick={() => handleItemNew({ parentId: selectedId })}
                    >   
                        <CommonIcons name={icons.add} />
                        <Box sx={{ ml: 1 }}>
                            {t('new')}
                        </Box>
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderCategoryHierachy = () => {
        return (
            <Grid container spacing={0}>
                                
                <Grid item md={5.5} sm={12} xs={12}>    
                    {
                        RenderCategoryLevel1()
                    }
                </Grid>

                <Grid item md={1} sm={12} xs={12} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                    <CommonIcons name={icons.chevronRight} />
                </Grid>

                <Grid item md={5.5} sm={12} xs={12}>    
                    {
                        RenderCategoryLevel2()
                    }
                </Grid>

            </Grid>
        )
    }

    const RenderFormChild = () => {
        return (
            <StyledCard>
                <Grid container spacing={1}>    

                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t('categoryHierarchy')}
                            subTitle={t('setForFurtherLevelOfCategory')}
                        />

                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>                       
                        {
                            RenderCategoryHierachy()
                        }
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        if(!values) return <></>

        return(
            
            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>                          
                        <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('information')}
                                subTitle={t('provideDetailsOfTheProfile')}
                            />

                        </Grid>

                        <Grid item xs={12} sm={12} md={9}>                       
                            <Grid container spacing={1}>

                                <Grid item xs={12} sm={12} md={6} textAlign="left">
                                    <FormControlLabel 
                                        control={
                                            <Switch 
                                                name="status" 
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        } 
                                        label={
                                            Boolean(values.status)? t('enabled') : t('disabled')
                                        }
                                    />
                                </Grid> 

                                <Grid item xs={6}>    
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"categoryName"}
                                        label={t('categoryName')}
                                        type="text"                                         
                                        variant="outlined"
                                        value={values.categoryName}
                                        onChange={handleChange}
                                        error={touched.categoryName && Boolean(errors.categoryName)}
                                        helperText={touched.categoryName && errors.categoryName}
                                    />
                                </Grid>

                                <Grid item xs={6}>    
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t('description')}
                                        type="text"                                         
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2}}>
                    <Grid item md={12} textAlign={"right"}>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ 
                                mt: 3,
                                mb: 2
                            }}
                            // loading={isLoading}
                        >
                            {t('saveChanges')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
                
        )
    }

    return (
        <Box>
            {  
                getHtmlHead({ routeType, title: headTitles.updateCategory })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('updateCategory')}
                breadcrumbs={[
                    {
                        displayText: t('categories'),
                        props: {
                            href: getRoute("products/categories")
                        }
                    },
                    {
                        displayText: t('updateCategory'),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            { RenderForm() }

            { RenderFormChild() }

        </Box>
    )
}